/**
 * Welcome opt-in popup.
 *
 * Step 1 subscribes to the Klaviyo list (single opt-in), which starts the welcome flow that
 * delivers the code. Step 2 writes the
 * chosen categories onto the profile as a separate call, so abandoning it costs no email.
 */
;(function () {
  const root = document.querySelector('[data-welcome-popup]')
  if (!root) return

  const companyId = root.dataset.companyId
  const listId = root.dataset.listId
  if (!companyId || !listId) return

  const STORAGE_KEY = 'husky:welcome-popup'
  const SESSION_KEY = 'husky:welcome-popup:seen'
  const KLAVIYO_REVISION = '2026-01-15'
  const MOBILE_QUERY = '(max-width: 767px)'
  const CONSENT_POLL_MS = 400
  const CONSENT_MAX_CHECKS = 300

  const overlay = root.querySelector('[data-welcome-popup-overlay]')
  const panel = root.querySelector('[data-welcome-popup-panel]')
  const form = root.querySelector('[data-welcome-popup-form]')
  const messageEl = root.querySelector('[data-welcome-popup-message]')
  const submitBtn = root.querySelector('[data-welcome-popup-submit]')
  const steps = {
    1: root.querySelector('[data-welcome-popup-step="1"]'),
    2: root.querySelector('[data-welcome-popup-step="2"]'),
    3: root.querySelector('[data-welcome-popup-step="3"]'),
  }

  let isOpen = false
  let subscribedEmail = ''
  let lastFocused = null
  let currentStep = 1
  let openTrigger = ''

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
    } catch (e) {
      return {}
    }
  }

  function writeState(status) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ status: status, at: Date.now() }))
    } catch (e) {
      /* private mode: fall back to the session guard only */
    }
  }

  function shouldShow() {
    const state = readState()
    if (state.status === 'subscribed') return false

    if (state.status === 'dismissed' && state.at) {
      const cooldownDays = parseInt(root.dataset.cooldownDays, 10) || 30
      if (Date.now() - state.at < cooldownDays * 86400000) return false
    }

    try {
      if (sessionStorage.getItem(SESSION_KEY)) return false
    } catch (e) {
      /* private mode: the popup may show once more per page load */
    }

    return true
  }

  // Analytics goes through the dataLayer like the rest of the theme; the GTM to Matomo
  // mapping is owned in GTM. Triggers differ by breakpoint, so every event carries the
  // device it fired on.
  function track(event, extra) {
    const payload = { event: event, device: window.matchMedia(MOBILE_QUERY).matches ? 'mobile' : 'desktop' }
    if (extra) {
      Object.keys(extra).forEach(function (key) {
        payload[key] = extra[key]
      })
    }
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(payload)
  }

  function showStep(n) {
    currentStep = n
    Object.keys(steps).forEach(function (key) {
      if (steps[key]) steps[key].classList.toggle('hidden', key !== String(n))
    })
    root.setAttribute('aria-labelledby', n === 1 ? 'welcome-popup-title' : 'welcome-popup-title-' + n)

    const focusTarget = steps[n] && steps[n].querySelector('input, button')
    if (focusTarget) focusTarget.focus()
  }

  function focusables() {
    return Array.prototype.filter.call(
      root.querySelectorAll('a[href], button, input, [tabindex]:not([tabindex="-1"])'),
      function (el) {
        return el.offsetParent !== null && !el.disabled
      }
    )
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      close('dismissed')
      return
    }
    if (e.key !== 'Tab') return

    const items = focusables()
    if (!items.length) return

    const first = items[0]
    const last = items[items.length - 1]

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  // The Trusted Shops badge is fixed at the maximum z-index, so on mobile it sits on
  // top of the submit button. Nothing can stack above it, so hide it while the popup
  // is open. Opacity rather than display: display none tears the widget down and it
  // does not come back when the property is cleared.
  function toggleTrustBadge(hidden) {
    const badge = document.querySelector('[id^="trustbadge-container-"]')
    if (!badge) return
    badge.style.opacity = hidden ? '0' : ''
    badge.style.pointerEvents = hidden ? 'none' : ''
  }

  function open() {
    if (isOpen) return
    isOpen = true
    lastFocused = document.activeElement
    toggleTrustBadge(true)

    try {
      sessionStorage.setItem(SESSION_KEY, '1')
    } catch (e) {
      /* nothing to do */
    }

    root.classList.remove('hidden')
    root.classList.add('flex')
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeydown)

    // translate-y-full only exists below md, so removing it slides the mobile sheet up
    // and is a no-op on desktop, where md:translate-y-0 already holds.
    requestAnimationFrame(function () {
      if (overlay) overlay.classList.remove('opacity-0')
      if (panel) panel.classList.remove('opacity-0', 'translate-y-full')
    })

    showStep(1)
    track('welcome_popup_shown', { trigger: openTrigger })
  }

  function close(status) {
    if (!isOpen) return
    isOpen = false

    // Only record a dismissal when the visitor never subscribed, so the cooldown
    // does not overwrite the permanent "subscribed" state.
    if (status === 'dismissed' && readState().status !== 'subscribed') {
      writeState('dismissed')
      track('welcome_popup_dismissed', { trigger: openTrigger, step: currentStep })
    }

    if (overlay) overlay.classList.add('opacity-0')
    if (panel) panel.classList.add('opacity-0', 'translate-y-full')
    document.removeEventListener('keydown', onKeydown)
    document.body.style.overflow = ''

    setTimeout(function () {
      root.classList.add('hidden')
      root.classList.remove('flex')
      toggleTrustBadge(false)
    }, 300)

    if (lastFocused && lastFocused.focus) lastFocused.focus()
  }

  function showError(text) {
    if (!messageEl) return
    messageEl.textContent = text
    messageEl.classList.remove('hidden')
  }

  function klaviyoPost(path, body) {
    return fetch('https://a.klaviyo.com/client/' + path + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    })
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault()

      const emailInput = form.querySelector('[name="email"]')
      const email = emailInput && emailInput.value ? emailInput.value.trim() : ''
      if (!email) return

      if (messageEl) messageEl.classList.add('hidden')
      if (submitBtn) submitBtn.disabled = true

      klaviyoPost('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Shopify theme welcome popup',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  // custom_source above lands on the consent record and cannot be
                  // segmented on directly. Mirror it onto the profile so Klaviyo
                  // segments and flows can split popup signups from the homepage form.
                  properties: { signup_source: 'welcome_popup' },
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId,
              },
            },
          },
        },
      })
        .then(function (res) {
          if (res.status !== 202) throw new Error('Klaviyo responded ' + res.status)
          subscribedEmail = email
          writeState('subscribed')
          track('welcome_popup_subscribed', { trigger: openTrigger })
          showStep(2)
        })
        .catch(function (err) {
          showError(root.dataset.errorMessage || '')
          track('welcome_popup_error', { reason: err && err.message ? err.message : 'request failed' })
          if (submitBtn) submitBtn.disabled = false
        })
    })
  }

  const selectedSegments = []

  root.querySelectorAll('[data-welcome-popup-segment]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const value = btn.dataset.welcomePopupSegment
      const index = selectedSegments.indexOf(value)

      if (index === -1) {
        selectedSegments.push(value)
        btn.setAttribute('aria-pressed', 'true')
      } else {
        selectedSegments.splice(index, 1)
        btn.setAttribute('aria-pressed', 'false')
      }
    })
  })

  function saveSegments() {
    if (!subscribedEmail || !selectedSegments.length) {
      showStep(3)
      return
    }

    klaviyoPost('profiles', {
      data: {
        type: 'profile',
        attributes: {
          email: subscribedEmail,
          properties: { shopping_for: selectedSegments },
        },
      },
    }).catch(function () {
      /* segmentation is a bonus: never block the confirmation step on it */
    })

    showStep(3)
  }

  const segmentsSubmit = root.querySelector('[data-welcome-popup-segments-submit]')
  const segmentsSkip = root.querySelector('[data-welcome-popup-segments-skip]')
  if (segmentsSubmit) segmentsSubmit.addEventListener('click', saveSegments)
  if (segmentsSkip)
    segmentsSkip.addEventListener('click', function () {
      showStep(3)
    })

  root.querySelectorAll('[data-welcome-popup-close]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      close(readState().status === 'subscribed' ? 'subscribed' : 'dismissed')
    })
  })

  if (overlay) {
    overlay.addEventListener('click', function () {
      close(readState().status === 'subscribed' ? 'subscribed' : 'dismissed')
    })
  }

  if (!shouldShow()) return

  const isMobile = window.matchMedia(MOBILE_QUERY).matches
  const delaySeconds = parseInt(isMobile ? root.dataset.mobileDelay : root.dataset.desktopDelay, 10)
  const scrollPercent = parseInt(isMobile ? root.dataset.mobileScroll : root.dataset.desktopScroll, 10)

  const timer = setTimeout(
    function () {
      trigger('delay')
    },
    (delaySeconds || 10) * 1000
  )

  function onScroll() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight
    if (scrollable <= 0) return

    const progress = (window.scrollY / scrollable) * 100
    if (progress >= (scrollPercent || 35)) trigger('scroll')
  }

  // Shopify's privacy banner is fixed at z-index 2000000, so it covers the lower half
  // of the panel and buries the decline link. Checked by height rather than offsetParent,
  // which is always null on a fixed element.
  function consentBannerVisible() {
    const banner = document.getElementById('shopify-pc__banner')
    return !!banner && banner.getBoundingClientRect().height > 0
  }

  function trigger(source) {
    clearTimeout(timer)
    window.removeEventListener('scroll', onScroll)
    openTrigger = source

    if (!consentBannerVisible()) {
      open()
      return
    }

    // Give up rather than poll forever if the visitor never answers the banner.
    let waited = 0
    const consentPoll = setInterval(function () {
      waited += 1
      if (!consentBannerVisible()) {
        clearInterval(consentPoll)
        open()
      } else if (waited > CONSENT_MAX_CHECKS) {
        clearInterval(consentPoll)
      }
    }, CONSENT_POLL_MS)
  }

  window.addEventListener('scroll', onScroll, { passive: true })
})()
